/*
 * <card-swatches> — mini color-swatch row on product cards.
 *
 * Liquid emits every sibling as a <li.card-color-swatches__item> plus one
 * trailing <li.card-color-swatches__more> chip. This element measures the
 * container at runtime, hides overflow items and writes the "+N" count
 * onto the chip. When everything fits, the chip is hidden entirely.
 *
 * Why JS and not pure CSS: CSS can clip overflow but can't tell us how
 * many items got clipped, which is the number the chip needs to show.
 *
 * ResizeObserver covers grid breakpoint flips and font-load reflows so
 * the row stays correct without a window resize listener.
 */
class CardSwatches extends HTMLElement {
    connectedCallback() {
        this.items = Array.from(this.querySelectorAll('li.card-color-swatches__item'));
        this.more = this.querySelector('li.card-color-swatches__more');
        this.moreLabel = this.more ? this.more.querySelector('span') : null;
        if (!this.items.length) return;

        this.fit();
        this.ro = new ResizeObserver(() => this.fit());
        this.ro.observe(this);

        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(() => this.fit());
        }
    }

    disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
    }

    fit() {
        const containerW = this.clientWidth;
        if (containerW <= 0) return;

        // Reset to "all visible, no chip" so measurements are accurate.
        this.items.forEach(li => { li.hidden = false; });
        if (this.more) this.more.hidden = true;

        const gap = parseFloat(getComputedStyle(this.firstElementChild || this).gap) || 0;
        const widths = this.items.map(li => li.offsetWidth);
        const totalAll = widths.reduce((s, w) => s + w, 0)
                       + gap * Math.max(0, widths.length - 1);

        if (totalAll <= containerW) return;
        if (!this.more) return;

        // Need clipping: reveal chip, fit as many items as we can before it.
        this.more.hidden = false;
        const chipW = this.more.offsetWidth;
        const budget = containerW - chipW - gap;
        let used = 0;
        let visible = 0;
        for (let i = 0; i < this.items.length; i++) {
            const next = used + widths[i] + (i > 0 ? gap : 0);
            if (next > budget) break;
            used = next;
            visible++;
        }
        for (let i = visible; i < this.items.length; i++) {
            this.items[i].hidden = true;
        }
        const hidden = this.items.length - visible;
        if (this.moreLabel) this.moreLabel.textContent = '+' + hidden;
    }
}

if (!customElements.get('card-swatches')) {
    customElements.define('card-swatches', CardSwatches);
}
